// Shared GS ("Under the Lights") primitives for the Season Dashboard.
// Colors come from designTokens GS via inline style — NEVER Tailwind named
// color utilities (the .light theme remaps them; see designTokens.ts header).

import React from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { GS, cardShadow } from '../../lib/designTokens';
import { shortName } from '../../lib/streak-signals';

export { shortName };

/** Gentle scroll-reveal for dashboard sections; no-op under prefers-reduced-motion. */
export const FadeIn = ({ children }: { children: React.ReactNode }) => {
  const reduce = useReducedMotion();
  if (reduce) return <>{children}</>;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.12 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

export const Card = ({ children, style, className, id }: {
  children: React.ReactNode; style?: React.CSSProperties; className?: string; id?: string;
}) => (
  <div
    id={id}
    className={`rounded-3xl overflow-hidden ${className || ''}`}
    style={{ background: GS.panel, boxShadow: cardShadow, ...style }}
  >
    {children}
  </div>
);

export const SectionHeader = ({ title, sub, action, id }: {
  title: string; sub?: string; action?: React.ReactNode; id?: string;
}) => (
  <div id={id} className="flex items-end justify-between gap-3 px-1 pt-6 pb-2 scroll-mt-24">
    <div>
      <p className="text-[11px] font-black uppercase" style={{ color: GS.greenInk, letterSpacing: '0.16em' }}>{title}</p>
      {sub && <p className="text-[10px] font-bold mt-0.5" style={{ color: GS.ink3 }}>{sub}</p>}
    </div>
    {action}
  </div>
);

export const JerseyChip = ({ number, tone = 'default', size = 'md' }: {
  number: string | number; tone?: 'default' | 'hot' | 'cold'; size?: 'md' | 'lg';
}) => {
  const dim = size === 'lg' ? 'w-11 h-11 text-base' : 'w-9 h-9 text-xs';
  const styles: React.CSSProperties =
    tone === 'hot' ? { background: `linear-gradient(150deg, ${GS.green}, ${GS.green2})`, color: '#FFFFFF', boxShadow: `0 3px 12px ${GS.greenGlow}` }
    : tone === 'cold' ? { background: '#E4EEF4', color: '#3E7FA6' }
    : { background: GS.panel2, color: GS.ink, border: `1px solid ${GS.line}` };
  return (
    <div className={`${dim} rounded-xl flex items-center justify-center font-black shrink-0`} style={styles}>
      {number}
    </div>
  );
};

/** Dark 0–99 badge (Command / DSS family — same visual language as OVR/xOVR). */
export const ScoreBadge = ({ score, label }: { score: number; label: string }) => (
  <div className="w-10 h-10 rounded-xl flex flex-col items-center justify-center shrink-0" style={{ background: GS.ink, color: '#FFFFFF' }}>
    <span className="text-sm font-black leading-none">{score}</span>
    <span className="text-[5.5px] font-extrabold tracking-widest mt-0.5" style={{ color: GS.ink3 }}>{label}</span>
  </div>
);

export const EmDash = () => <span style={{ color: GS.ink3 }}>—</span>;

/** Single-series sparkline (validated series green, endpoint emphasized). */
export const Sparkline = ({ values, width = 300, height = 56 }: { values: number[]; width?: number; height?: number }) => {
  if (values.length < 2) return null;
  const max = Math.max(...values), min = Math.min(...values);
  const span = max - min || 1;
  const pts = values.map((v, i) => {
    const x = (i * width) / (values.length - 1);
    const y = height - 6 - ((v - min) / span) * (height - 12);
    return [x, y] as const;
  });
  const last = pts[pts.length - 1];
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
      <polyline
        points={pts.map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')}
        fill="none" stroke="#0EA372" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      />
      <circle cx={last[0]} cy={last[1]} r="4" fill="#0EA372" stroke={GS.panel} strokeWidth="2" />
    </svg>
  );
};
